<template>
  <v-dialog
    :value="dialog"
    persistent
    max-width="1200"
    @click:outside="$emit('close')"
  >
    <v-card class="AIChatSettings">
      <v-system-bar color="primary" dark height="40">
        <v-icon class="mr-2">mdi-settings</v-icon>
        <span class="text-subtitle-1">AI Chat Configuration</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-system-bar>

      <v-card-title class="py-4 px-6">
        <h2 class="text-h5 font-weight-regular">
          {{
            this?.currentSession?.workspace?.name || this.chatWorkspace?.name
              ? this.currentSession?.workspace?.name || this.chatWorkspace?.name
              : 'Your'
          }}
          Settings
        </h2>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <v-container fluid class="pa-0">
          <v-row>
            <v-col cols="12" md="7" class="pr-md-6">
              <div class="setting-section">
                <div class="d-flex align-center mb-4">
                  <v-icon color="primary" size="24">mdi-robot</v-icon>
                  <h3 class="text-h6 font-weight-medium ml-3 mb-0">
                    Chat Model
                  </h3>
                </div>
                <v-card outlined class="pa-4">
                  <v-autocomplete
                    outlined
                    dense
                    hide-details
                    label="Select AI Model"
                    :items="models"
                    v-model="selectedModel"
                    item-text="label"
                    item-value="value"
                    :disabled="disableSettings()"
                    @change="(value) => updateSettings({ model: value })"
                    class="mb-0"
                  ></v-autocomplete>
                </v-card>
              </div>

              <div class="setting-section">
                <div class="d-flex align-center mb-4">
                  <v-icon color="primary" size="24"
                    >mdi-text-box-outline</v-icon
                  >
                  <h3 class="text-h6 font-weight-medium ml-3 mb-0">
                    System Prompt
                  </h3>
                </div>
                <v-card outlined>
                  <v-card-text class="pa-4">
                    <v-textarea
                      outlined
                      dense
                      hide-details
                      placeholder="Enter system prompt"
                      :rows="6"
                      :disabled="disableSettings()"
                      v-model="systemPromptText"
                      class="mb-4"
                    ></v-textarea>

                    <v-divider class="mb-4"></v-divider>

                    <div class="d-flex align-center">
                      <v-autocomplete
                        outlined
                        dense
                        hide-details
                        label="Load Saved Prompt"
                        :items="systemPrompts"
                        v-model="selectedSystemPrompt"
                        item-text="name"
                        item-value="id"
                        :disabled="disableSettings()"
                        @change="(value) => updateSelectedSystemPrompt(value)"
                        :loading="systemPromptLoading"
                        :append-outer-icon="'mdi-reload'"
                        @click:append-outer="this.getSystemPrompts"
                        clearable
                        class="mr-3"
                      ></v-autocomplete>

                      <v-btn
                        color="primary"
                        :disabled="disableSettings() || !systemPromptText"
                        @click.stop="saveSystemPrompt()"
                        :loading="updateSystemPromptLoading"
                        class="px-6"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Save
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-col>

            <v-col cols="12" md="5">
              <div class="setting-section">
                <div class="d-flex align-center mb-4">
                  <v-icon color="primary" size="24">mdi-lightning-bolt</v-icon>
                  <h3 class="text-h6 font-weight-medium ml-3 mb-0">
                    Action Set
                  </h3>
                </div>
                <v-card outlined>
                  <v-card-text class="pa-4">
                    <div v-if="!disableSettings()">
                      <div class="d-flex align-center mb-4">
                        <v-autocomplete
                          outlined
                          dense
                          hide-details
                          label="Select Action Set"
                          :items="toolSets"
                          v-model="selectedToolSet"
                          item-text="name"
                          item-value="id"
                          @change="(value) => selectToolSet(value)"
                          :loading="toolSetsLoading"
                          :append-outer-icon="'mdi-reload'"
                          @click:append-outer="this.getToolSets"
                          clearable
                          class="mr-3"
                        ></v-autocomplete>

                        <v-btn
                          color="primary"
                          @click.stop="isCreateToolSetOpen = true"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <div
                      v-if="disableSettings()"
                      class="subtitle-1 grey--text mb-4"
                    >
                      {{ this.selectedToolSetEntity?.name }}
                    </div>

                    <template v-if="selectedToolSetEntity">
                      <v-divider class="mb-4"></v-divider>

                      <div class="d-flex align-center mb-3">
                        <h4 class="subtitle-1 font-weight-medium mb-0">
                          Available Actions
                        </h4>
                        <v-btn
                          v-if="!disableSettings()"
                          icon
                          small
                          color="primary"
                          class="ml-2"
                          @click.stop="
                            setEditToolSet({ ...selectedToolSet });
                            isEditToolSetOpen = true;
                          "
                        >
                          <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                      </div>

                      <v-chip-group column>
                        <v-chip
                          v-for="tool in selectedToolSetEntity.tools"
                          :key="tool.id"
                          small
                          outlined
                          color="primary"
                        >
                          {{ tool?.name }}
                        </v-chip>
                      </v-chip-group>
                    </template>
                  </v-card-text>
                </v-card>
              </div>

              <div class="setting-section">
                <div class="d-flex align-center mb-4">
                  <v-icon color="primary" size="24">mdi-tune</v-icon>
                  <h3 class="text-h6 font-weight-medium ml-3 mb-0">
                    Advanced Settings
                  </h3>
                </div>
                <v-card outlined>
                  <v-card-text class="pa-4">
                    <div class="mb-6">
                      <div
                        class="d-flex align-center justify-space-between mb-2"
                      >
                        <h4 class="subtitle-1 font-weight-medium mb-0">
                          Max Tokens
                        </h4>
                        <span class="caption grey--text"
                          >({{ maxTokens }})</span
                        >
                      </div>
                      <v-slider
                        v-model="maxTokens"
                        :min="1000"
                        :max="15000"
                        :disabled="disableSettings()"
                        v-on:input="debounceMaxTokens"
                        hide-details
                        class="mt-0"
                      ></v-slider>
                    </div>

                    <v-divider class="mb-4"></v-divider>

                    <div class="mb-4">
                      <div
                        class="d-flex align-center justify-space-between mb-2"
                      >
                        <h4 class="subtitle-1 font-weight-medium mb-0">
                          Number of latest messages included
                        </h4>
                        <span
                          class="caption grey--text"
                          v-if="!includeAllHistory"
                          >({{ historyLimit }} messages)</span
                        >
                        <span class="caption grey--text" v-else
                          >(All messages)</span
                        >
                      </div>
                      <v-slider
                        v-model="historyLimit"
                        :min="0"
                        :max="50"
                        :disabled="disableSettings() || includeAllHistory"
                        v-on:input="debounceHistoryLimit"
                        hide-details
                        class="!mt-0"
                      ></v-slider>
                      <v-checkbox
                        v-if="isShAdmin()"
                        label="Include All History"
                        v-model="includeAllHistory"
                        @change="
                          updateSettings({
                            historyLimit: includeAllHistory
                              ? null
                              : historyLimit,
                          })
                        "
                        :disabled="disableSettings()"
                        hide-details
                        dense
                        class="mt-0"
                      ></v-checkbox>
                    </div>

                    <v-divider class="mb-4"></v-divider>

                    <div>
                      <h4 class="subtitle-1 font-weight-medium mb-3">
                        Display Options
                      </h4>
                      <v-checkbox
                        v-model="hideToolIO"
                        label="Hide Action Input/Output"
                        @change="updateAdditional()"
                        hide-details
                        dense
                      ></v-checkbox>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <create-tool-set
      :dialog="isCreateToolSetOpen"
      @close="isCreateToolSetOpen = false"
    ></create-tool-set>

    <tools-select
      :dialog="isEditToolSetOpen"
      :toolSets="selectedToolSetEntity?.tools"
      @close="isEditToolSetOpen = false"
      @toolsChanged="updateToolSetData"
    />
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import CreateToolSet from '@/components/modals/tool-set/create-tool-set/create-tool-set.vue';
import debounce from 'lodash/debounce';
import lodashGet from 'lodash/get';
import ToolsSelect from '@/views/ai-workspaces/create-workspace/tools-select/tools-select.vue';
import { availableAIModels, defaultModel } from '@/util/availableAIModels';
const {
  mapGetters: AIChatGetters,
  mapActions: AIChatActions,
  mapMutations: AIChatMutations,
} = createNamespacedHelpers('AIChat');

const { mapActions: workspaceActions } = createNamespacedHelpers('aiWorkspace');

const { mapGetters: customerGetters } = createNamespacedHelpers('customer');

export default {
  name: 'AIChatSettings',
  components: { ToolsSelect, CreateToolSet },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isCreateToolSetOpen: false,
      isEditToolSetOpen: false,
      systemPromptText: '',
      selectedToolSet: null,
      selectedToolSetEntity: null,
      selectedSystemPrompt: null,
      hideToolIO: null,
      maxTokens: 2000,
      historyLimit: 15,
      includeAllHistory: false,
      models: availableAIModels,
      selectedModel: defaultModel,
    };
  },
  computed: {
    ...AIChatGetters({
      toolSets: 'TOOL_SETS',
      toolSetsLoading: 'TOOL_SETS_LOADING',
      userSettings: 'USER_SETTINGS',
      systemPrompts: 'SYSTEM_PROMPTS',
      systemPromptLoading: 'SYSTEM_PROMPT_LOADING',
      updateSystemPromptLoading: 'UPDATE_SYSTEM_PROMPT_LOADING',
      currentToolSet: 'CURRENT_TOOL_SETS',
      currentSession: 'CURRENT_SESSION',
      chatWorkspace: 'GET_CHAT_WORKSPACE',
    }),
    ...customerGetters({
      customer: 'CUSTOMER',
      user: 'USER',
      customers: 'CUSTOMERS',
      selected_customer: 'SELECTED_CUSTOMER',
      featureFlags: 'FEATURE_FLAGS',
    }),
  },
  watch: {
    currentSession: {
      immediate: true,
      async handler(newVal, oldVal) {
        if (newVal?.workspaceId === oldVal?.workspaceId) {
          return;
        }
        if (!newVal.workspaceId && !this.chatWorkspace?.id) {
          await this.setSettingsFromUser();
          return;
        }
        await this.setSettingsFromWorkspace(
          newVal?.workspaceId || this.chatWorkspace?.id
        );
      },
    },
    chatWorkspace: {
      immediate: true,
      async handler(newVal, oldVal) {
        if (newVal?.id === oldVal?.id) {
          return;
        }

        if (!newVal?.id) {
          await this.setSettingsFromUser();
          return;
        }
        await this.setSettingsFromWorkspace(
          newVal?.id || this.chatWorkspace?.id
        );
      },
    },
    async 'selected_customer.customer_id'() {
      await this.getSystemPrompts();
      await this.getToolSets();
    },
  },
  methods: {
    ...AIChatActions([
      'updateUserSettings',
      'getToolSets',
      'getSystemPrompts',
      'updateSystemPrompt',
      'createSystemPrompt',
      'updateToolSet',
    ]),
    ...AIChatMutations({
      setSystemPrompts: 'SET_SYSTEM_PROMPTS',
      setEditToolSet: 'SET_EDIT_TOOL_SET',
    }),
    ...workspaceActions(['getWorkspace']),
    disableSettings() {
      return !!this.currentSession.workspaceId || !!this.chatWorkspace?.id;
    },
    async updateToolSetData(data) {
      if (!this.selectedToolSetEntity?.id) {
        return;
      }
      this.selectedToolSetEntity.tools = data;
      await this.updateToolSet({
        toolSetIds: data.map((tool) => tool.id),
        id: this.selectedToolSetEntity.id,
      });
    },
    isShAdmin() {
      return lodashGet(this.customer, 'customer_id') === 0;
    },
    async updateSettings(data) {
      if (this.disableSettings()) {
        return;
      }
      await this.updateUserSettings(data);
    },
    async selectToolSet(value) {
      this.selectedToolSetEntity = this.toolSets.find((s) => s.id === value);
      if (this.selectedToolSetEntity) {
        await this.updateSettings({ toolSetId: value });
      }
    },
    async saveSystemPrompt() {
      if (this.selectedSystemPrompt?.id) {
        await this.updateSystemPrompt({
          systemPromptId: this.selectedSystemPrompt.id,
          prompt: this.systemPromptText,
        });
        return;
      }
      const prompt = await this.createSystemPrompt({
        prompt: this.systemPromptText,
        name: this.systemPromptText,
      });
      this.systemPrompts.push(prompt);
      this.selectedSystemPrompt = prompt;
      this.systemPromptText = prompt.prompt;
      await this.updateSettings({ systemPromptId: prompt.id });
    },
    async updateSelectedSystemPrompt(value) {
      const systemPrompt = this.systemPrompts.find(
        (prompt) => prompt.id === value
      );
      await this.updateSettings({ systemPromptId: systemPrompt.id });
      this.systemPromptText = systemPrompt.prompt;
      this.selectedSystemPrompt = systemPrompt;
    },
    async updateAdditional() {
      await this.updateUserSettings({ hideToolIO: this.hideToolIO });
    },
    setSettingsFromUser() {
      this.selectedToolSet = this.toolSets.find(
        (set) => set.id === this.userSettings.toolSetId
      );
      this.selectedToolSetEntity = this.toolSets.find(
        (set) => set.id === this.userSettings.toolSetId
      );
      const systemPrompt = this.systemPrompts.find(
        (prompt) => prompt.id === this.userSettings.systemPromptId
      );
      this.selectedSystemPrompt = systemPrompt;
      this.systemPromptText = systemPrompt?.prompt;
      this.hideToolIO = this.userSettings.hideToolIO;
      this.selectedModel = this.models.find(
        (model) => model.value === this.userSettings.model
      );
      this.maxTokens = this.userSettings.maxTokens;
      this.historyLimit = this.userSettings.historyLimit;
      this.includeAllHistory =
        this.userSettings.historyLimit === null ||
        this.userSettings.historyLimit === undefined;
    },
    async setSettingsFromWorkspace(workspaceId) {
      if (!workspaceId) {
        return;
      }
      const workspace = await this.getWorkspace({ workspaceId });
      this.selectedToolSet = {
        name: workspace?.name,
        tools: workspace?.tools,
      };
      this.selectedToolSetEntity = {
        name: workspace?.name,
        tools: workspace?.tools,
      };
      const systemPrompt = workspace.SystemPrompt;
      this.selectedSystemPrompt = systemPrompt;
      this.systemPromptText = systemPrompt?.prompt;
      this.hideToolIO = this.userSettings.hideToolIO;
      this.selectedModel = this.models.find(
        (model) => model.value === workspace.model
      );
      this.maxTokens = workspace.maxTokens;
      this.historyLimit = workspace.historyLimit;
      this.includeAllHistory =
        workspace.historyLimit === null || workspace.historyLimit === undefined;
    },
    debounceMaxTokens: debounce(function (e) {
      this.maxTokens = e;
      this.updateSettings({ maxTokens: this.maxTokens });
    }, 1000),
    debounceHistoryLimit: debounce(function (e) {
      this.historyLimit = e;
      this.updateSettings({ historyLimit: this.historyLimit });
    }, 1000),
  },
  async created() {
    await this.getSystemPrompts();
    await this.getToolSets();
    if (this.currentSession?.workspaceId) {
      await this.setSettingsFromWorkspace(this.currentSession?.workspaceId);
    } else if (this.chatWorkspace?.id) {
      await this.setSettingsFromWorkspace(this.chatWorkspace?.id);
    } else {
      this.setSettingsFromUser();
    }
  },
};
</script>

<style lang="scss" scoped>
@import './ai-chat-settings';
</style>
